import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchJson } from '../api';
import StoreBadges from '../components/StoreBadges';
import { trackCreditsView } from '../lib/analytics';
import { formatCount, relativeTime } from '../lib/format';

const REASONS = {
  welcome: 'Welcome credits',
  vote_earned: 'Voting Booth vote',
  boost_spent: 'Boost',
  purchase: 'Bought in the app',
  refund: 'Refund',
  admin: 'Adjustment',
};

export function ledgerLabel(row) {
  return REASONS[row?.reason] || 'Credits';
}

/**
 * Credits (spec §1): earned in the booth, bought in the app, spent on boosts.
 * Fiat in, never out. The web shows the balance and history; buying happens
 * in the app (spec §4, web purchase is out of scope this round).
 */
export default function CreditsPage() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | auth | error

  useEffect(() => {
    let cancelled = false;
    fetchJson('/me/credits')
      .then((d) => {
        if (cancelled) return;
        setData(d || {});
        setStatus('ready');
        trackCreditsView({ credits: d?.credits ?? null });
      })
      .catch((err) => { if (!cancelled) setStatus(err.status === 401 || err.status === 403 ? 'auth' : 'error'); });
    return () => { cancelled = true; };
  }, []);

  const credits = Number(data?.credits || 0);
  const today = Number(data?.today_earned || 0);
  const cap = Number(data?.cap || 0);
  const ledger = Array.isArray(data?.ledger) ? data.ledger : [];
  const capPct = cap ? Math.min(100, Math.round((today / cap) * 100)) : 0;

  return (
    <main className="mx-auto w-full max-w-xl px-4 pb-10 pt-5">
      <h1 className="text-2xl font-semibold text-voto-fg">Credits</h1>

      {status === 'loading' && <p className="mt-6 text-voto-muted">Loading…</p>}
      {status === 'auth' && (
        <p className="mt-6 rounded-xl bg-voto-surface p-4 text-sm text-voto-muted ring-1 ring-voto-line">
          <Link to="/auth" className="font-semibold text-voto-coral hover:underline">Sign in</Link> to earn and keep credits.
        </p>
      )}
      {status === 'error' && (
        <p className="mt-6 rounded-lg bg-voto-coral/10 p-3 text-voto-coral">Unable to load your credits right now.</p>
      )}

      {status === 'ready' && (
        <>
          <section className="mt-4 rounded-2xl bg-voto-surface p-5 ring-1 ring-voto-line" aria-label="Balance">
            <p className="text-sm text-voto-muted">Balance</p>
            <p className="mt-1 text-4xl font-bold text-white" data-testid="credits-balance">
              {formatCount(credits)} <span className="text-base font-medium text-voto-muted">{credits === 1 ? 'credit' : 'credits'}</span>
            </p>
            {cap > 0 && (
              <div className="mt-4">
                <p className="flex justify-between text-sm text-voto-fg">
                  <span>Earned today</span>
                  <span className="text-voto-muted">{formatCount(today)} / {formatCount(cap)}</span>
                </p>
                <span className="mt-2 block h-1.5 overflow-hidden rounded-full bg-voto-raised" aria-hidden="true">
                  <span className="block h-full bg-voto-coral" style={{ width: `${capPct}%` }} />
                </span>
              </div>
            )}
            <Link
              to="/booth"
              className="mt-4 inline-flex rounded-[12px] bg-voto-coral px-4 py-2 text-sm font-semibold text-white transition hover:bg-voto-coralDeep"
            >
              Earn credits in the Voting Booth
            </Link>
          </section>

          <section className="mt-4 rounded-2xl bg-voto-surface p-5 ring-1 ring-voto-line" aria-labelledby="buy-title">
            <h2 id="buy-title" className="text-base font-semibold text-white">Buy credits in the Voto app</h2>
            <p className="mt-1 text-sm text-voto-muted">25 credits for $0.99. Credits never expire and work on the web too.</p>
            <StoreBadges className="mt-3" size="h-10" />
          </section>

          <section className="mt-6" aria-labelledby="ledger-title">
            <h2 id="ledger-title" className="text-sm font-semibold text-voto-muted">History</h2>
            {ledger.length === 0 ? (
              <p className="mt-3 text-sm text-voto-muted">Nothing yet. Vote in the booth to earn your first credits.</p>
            ) : (
              <ul className="mt-2 divide-y divide-voto-line" data-testid="credits-ledger">
                {ledger.map((row, i) => {
                  const delta = Number(row.delta || 0);
                  return (
                    <li key={row.id ?? `${row.reason}-${row.created_at}-${i}`} className="flex items-center gap-3 py-2.5 text-sm">
                      <span className="min-w-0 flex-1 text-voto-fg">{ledgerLabel(row)}</span>
                      {row.created_at && <span className="flex-none text-xs text-voto-muted">{relativeTime(row.created_at)}</span>}
                      <span className={`w-14 flex-none text-right font-semibold ${delta >= 0 ? 'text-voto-green' : 'text-voto-muted'}`}>
                        {delta >= 0 ? `+${formatCount(delta)}` : `−${formatCount(Math.abs(delta))}`}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </>
      )}
    </main>
  );
}
